import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar, Plus, Minus, Calculator, Clock } from 'lucide-react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';

interface DateDifference {
  years: number;
  months: number;
  days: number;
  totalDays: number;
  totalWeeks: number;
  totalHours: number;
  totalMinutes: number;
  totalSeconds: number;
}

export const DateCalculatorTool: React.FC = () => {
  const { t } = useTranslation();
  const [mode, setMode] = useState<'difference' | 'add' | 'subtract'>('difference');
  
  // Date difference mode
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [dateDifference, setDateDifference] = useState<DateDifference | null>(null);
  
  // Add/subtract mode
  const [baseDate, setBaseDate] = useState('');
  const [addYears, setAddYears] = useState(0);
  const [addMonths, setAddMonths] = useState(0);
  const [addDays, setAddDays] = useState(0);
  const [resultDate, setResultDate] = useState<Date | null>(null);

  const calculateDateDifference = (start: Date, end: Date): DateDifference => {
    const startTime = start.getTime();
    const endTime = end.getTime();
    const timeDiff = Math.abs(endTime - startTime);
    
    // Calculate total units
    const totalSeconds = Math.floor(timeDiff / 1000);
    const totalMinutes = Math.floor(totalSeconds / 60);
    const totalHours = Math.floor(totalMinutes / 60);
    const totalDays = Math.floor(totalHours / 24);
    const totalWeeks = Math.floor(totalDays / 7);
    
    // Calculate years, months, days
    let years = 0;
    let months = 0;
    let days = 0;
    
    const earlierDate = new Date(Math.min(startTime, endTime));
    const laterDate = new Date(Math.max(startTime, endTime));
    
    // Calculate years
    years = laterDate.getFullYear() - earlierDate.getFullYear();
    
    // Calculate months
    months = laterDate.getMonth() - earlierDate.getMonth();
    if (months < 0) {
      years--;
      months += 12;
    }
    
    // Calculate days
    days = laterDate.getDate() - earlierDate.getDate();
    if (days < 0) {
      months--;
      if (months < 0) {
        years--;
        months += 12;
      }
      // Get days in previous month
      const prevMonth = new Date(laterDate.getFullYear(), laterDate.getMonth(), 0);
      days += prevMonth.getDate();
    }
    
    return {
      years,
      months,
      days,
      totalDays,
      totalWeeks,
      totalHours,
      totalMinutes,
      totalSeconds
    };
  };

  const addSubtractDate = (date: Date, years: number, months: number, days: number): Date => {
    const result = new Date(date);
    
    // Add/subtract years
    result.setFullYear(result.getFullYear() + years);
    
    // Add/subtract months
    result.setMonth(result.getMonth() + months);
    
    // Add/subtract days
    result.setDate(result.getDate() + days);
    
    return result;
  };

  // Calculate date difference when dates change
  useEffect(() => {
    if (mode === 'difference' && startDate && endDate) {
      try {
        const start = new Date(startDate);
        const end = new Date(endDate);
        
        if (!isNaN(start.getTime()) && !isNaN(end.getTime())) {
          setDateDifference(calculateDateDifference(start, end));
        }
      } catch (error) {
        setDateDifference(null);
      }
    }
  }, [mode, startDate, endDate]);

  // Calculate result date when inputs change
  useEffect(() => {
    if ((mode === 'add' || mode === 'subtract') && baseDate) {
      try {
        const base = new Date(baseDate);
        
        if (!isNaN(base.getTime())) {
          const multiplier = mode === 'subtract' ? -1 : 1;
          const result = addSubtractDate(
            base,
            addYears * multiplier,
            addMonths * multiplier,
            addDays * multiplier
          );
          setResultDate(result);
        }
      } catch (error) {
        setResultDate(null);
      }
    }
  }, [mode, baseDate, addYears, addMonths, addDays]);

  const formatDate = (date: Date): string => {
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const setToday = (setter: (value: string) => void) => {
    const today = new Date();
    const dateString = today.toISOString().split('T')[0];
    setter(dateString);
  };

  const modes = [
    { id: 'difference', label: 'Calculate Difference', icon: Calculator },
    { id: 'add', label: 'Add Time', icon: Plus },
    { id: 'subtract', label: 'Subtract Time', icon: Minus }
  ];

  return (
    <div className="space-y-6">
      {/* Mode Selection */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Calendar className="w-5 h-5" />
            <span>Date Calculator</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {modes.map((modeOption) => {
              const IconComponent = modeOption.icon;
              return (
                <Button
                  key={modeOption.id}
                  onClick={() => setMode(modeOption.id as any)}
                  variant={mode === modeOption.id ? 'primary' : 'outline'}
                  className="flex items-center space-x-2"
                >
                  <IconComponent className="w-4 h-4" />
                  <span>{modeOption.label}</span>
                </Button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Date Difference Mode */}
      {mode === 'difference' && (
        <Card>
          <CardHeader>
            <CardTitle>Calculate Date Difference</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Start Date
                </label>
                <div className="flex space-x-2">
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  />
                  <Button
                    onClick={() => setToday(setStartDate)}
                    variant="outline"
                    size="sm"
                  >
                    Today
                  </Button>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  End Date
                </label>
                <div className="flex space-x-2">
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  />
                  <Button
                    onClick={() => setToday(setEndDate)}
                    variant="outline"
                    size="sm"
                  >
                    Today
                  </Button>
                </div>
              </div>
            </div>

            {dateDifference && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="text-center p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                    <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                      {dateDifference.years}
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">Years</div>
                  </div>
                  
                  <div className="text-center p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                    <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                      {dateDifference.months}
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">Months</div>
                  </div>
                  
                  <div className="text-center p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                    <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                      {dateDifference.days}
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">Days</div>
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div className="text-center p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <div className="font-bold text-gray-900 dark:text-gray-100">
                      {dateDifference.totalDays.toLocaleString()}
                    </div>
                    <div className="text-gray-600 dark:text-gray-400">Total Days</div>
                  </div>
                  
                  <div className="text-center p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <div className="font-bold text-gray-900 dark:text-gray-100">
                      {dateDifference.totalWeeks.toLocaleString()}
                    </div>
                    <div className="text-gray-600 dark:text-gray-400">Total Weeks</div>
                  </div>
                  
                  <div className="text-center p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <div className="font-bold text-gray-900 dark:text-gray-100">
                      {dateDifference.totalHours.toLocaleString()}
                    </div>
                    <div className="text-gray-600 dark:text-gray-400">Total Hours</div>
                  </div>
                  
                  <div className="text-center p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <div className="font-bold text-gray-900 dark:text-gray-100">
                      {dateDifference.totalMinutes.toLocaleString()}
                    </div>
                    <div className="text-gray-600 dark:text-gray-400">Total Minutes</div>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Add/Subtract Mode */}
      {(mode === 'add' || mode === 'subtract') && (
        <Card>
          <CardHeader>
            <CardTitle>
              {mode === 'add' ? 'Add Time to Date' : 'Subtract Time from Date'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Base Date
                </label>
                <div className="flex space-x-2">
                  <input
                    type="date"
                    value={baseDate}
                    onChange={(e) => setBaseDate(e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  />
                  <Button
                    onClick={() => setToday(setBaseDate)}
                    variant="outline"
                    size="sm"
                  >
                    Today
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Input
                  label="Years"
                  type="number"
                  value={addYears}
                  onChange={(e) => setAddYears(parseInt(e.target.value) || 0)}
                  placeholder="0"
                />
                
                <Input
                  label="Months"
                  type="number"
                  value={addMonths}
                  onChange={(e) => setAddMonths(parseInt(e.target.value) || 0)}
                  placeholder="0"
                />
                
                <Input
                  label="Days"
                  type="number"
                  value={addDays}
                  onChange={(e) => setAddDays(parseInt(e.target.value) || 0)}
                  placeholder="0"
                />
              </div>

              {resultDate && (
                <div className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-lg">
                  <div className="text-center">
                    <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                      Result Date
                    </div>
                    <div className="text-xl font-bold text-gray-900 dark:text-gray-100">
                      {formatDate(resultDate)}
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      {resultDate.toISOString().split('T')[0]}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Clock className="w-5 h-5" />
            <span>Quick Calculations</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <Button
              onClick={() => {
                const today = new Date();
                const nextWeek = new Date(today);
                nextWeek.setDate(today.getDate() + 7);
                setStartDate(today.toISOString().split('T')[0]);
                setEndDate(nextWeek.toISOString().split('T')[0]);
                setMode('difference');
              }}
              variant="outline"
              size="sm"
            >
              Next Week
            </Button>
            
            <Button
              onClick={() => {
                const today = new Date();
                const nextMonth = new Date(today);
                nextMonth.setMonth(today.getMonth() + 1);
                setStartDate(today.toISOString().split('T')[0]);
                setEndDate(nextMonth.toISOString().split('T')[0]);
                setMode('difference');
              }}
              variant="outline"
              size="sm"
            >
              Next Month
            </Button>
            
            <Button
              onClick={() => {
                const today = new Date();
                const nextYear = new Date(today);
                nextYear.setFullYear(today.getFullYear() + 1);
                setStartDate(today.toISOString().split('T')[0]);
                setEndDate(nextYear.toISOString().split('T')[0]);
                setMode('difference');
              }}
              variant="outline"
              size="sm"
            >
              Next Year
            </Button>
            
            <Button
              onClick={() => {
                const today = new Date();
                const birthday = new Date(today.getFullYear(), 0, 1); // Jan 1st
                setStartDate(birthday.toISOString().split('T')[0]);
                setEndDate(today.toISOString().split('T')[0]);
                setMode('difference');
              }}
              variant="outline"
              size="sm"
            >
              This Year
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Usage Guide */}
      <Card>
        <CardHeader>
          <CardTitle>Usage Guide</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
            <div>
              <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">Calculate Difference:</h4>
              <ul className="space-y-1 text-gray-600 dark:text-gray-400">
                <li>• Enter start and end dates</li>
                <li>• Get precise years, months, and days</li>
                <li>• View total time in various units</li>
                <li>• Use "Today" button for current date</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">Add/Subtract Time:</h4>
              <ul className="space-y-1 text-gray-600 dark:text-gray-400">
                <li>• Choose a base date</li>
                <li>• Enter years, months, or days to add/subtract</li>
                <li>• Get the resulting date</li>
                <li>• Perfect for deadline calculations</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
